/**
 * @file ReprendreLeSport.tsx
 * @summary Page service
 *
 * Page descriptive d'un service proposé par la box (cours WOD, débutant, hybride, reprise du sport).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { Link } from "react-router-dom";

const URL = "https://crossfitchistera.fr/reprendre-le-sport";

const faq = [
  {
    "@type": "Question",
    name: "À quel âge peut-on reprendre le sport au CrossFit ?",
    acceptedAnswer: { "@type": "Answer", text: "À tout âge. CrossFit Chistera accueille des adhérents de 16 à 75 ans, avec un cours dédié pour les seniors et des charges/mouvements adaptés à chaque profil." },
  },
  {
    "@type": "Question",
    name: "Combien de temps avant de retrouver la forme ?",
    acceptedAnswer: { "@type": "Answer", text: "La plupart des adhérents en reprise ressentent un net regain d'énergie et de tonus en 4 à 6 semaines à raison de 2 séances par semaine. Les progrès sur la force et l'endurance sont visibles entre 8 et 12 semaines." },
  },
  {
    "@type": "Question",
    name: "Et si j'ai mal au dos ou aux articulations ?",
    acceptedAnswer: { "@type": "Answer", text: "Le CrossFit bien encadré est souvent recommandé pour renforcer le dos et la sangle abdominale, à condition d'adapter les mouvements. Les coachs Chistera (Master STAPS, BPJEPS) ajustent les exercices à votre situation." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Reprise du sport adulte",
    provider: { "@id": "https://crossfitchistera.fr/#localbusiness" },
    url: URL,
    description: "Reprendre le sport en toute sécurité à Saint-Pierre-de-Chandieu (Lyon Est). Méthode complète, coachs diplômés, cours adaptés à tous les âges et profils.",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

export default function ReprendreLeSport() {
  return (
    <>
      <SEO
        title="Reprendre le sport Lyon Est — Chistera"
        description="Reprendre le sport à Saint-Pierre-de-Chandieu : cours collectifs encadrés, mouvements adaptés à votre niveau, sport santé et bien-être. 2 séances d'essai gratuites."
        canonical={URL}
        jsonLd={jsonLd}
      />
      <ServicePageLayout
        breadcrumbs={[{ label: "Reprendre le sport" }]}
        h1="Reprendre le sport à tout âge — Méthode complète"
        intro="Vous voulez retrouver la forme, vous renforcer, dormir mieux, perdre du poids ou simplement bouger ? Le CrossFit, encadré par des coachs diplômés et adapté à votre niveau, est l'une des méthodes les plus efficaces pour reprendre le sport durablement. 2 séances d'essai gratuites chez CrossFit Chistera."
      >
        <h2>Pourquoi le CrossFit pour reprendre le sport ?</h2>
        <p>
          Reprendre le sport après plusieurs mois — ou plusieurs années — d'inactivité demande une méthode <strong>complète</strong>, <strong>progressive</strong> et <strong>encadrée</strong>. Le CrossFit coche les trois cases : renforcement musculaire, gymnastique, haltérophilie et cardio dans la même séance, charges adaptées à votre niveau, coach diplômé qui corrige chaque mouvement.
        </p>
        <p>
          Pas besoin d'« être en forme » avant de commencer. C'est même l'inverse : on vient au CrossFit <em>parce qu'on veut</em> retrouver la forme. Voir aussi notre page <Link to="/crossfit-debutant">CrossFit débutant</Link>.
        </p>

        <h2>Les bénéfices que vous allez ressentir</h2>
        <ul>
          <li><strong>Plus d'énergie au quotidien</strong> dès les premières semaines.</li>
          <li><strong>Un meilleur sommeil</strong>, plus profond et plus réparateur.</li>
          <li><strong>Un dos plus solide</strong> grâce au renforcement de la sangle abdominale et des chaînes posturales.</li>
          <li><strong>Une perte de masse grasse</strong> progressive si l'alimentation suit.</li>
          <li><strong>Un mental plus apaisé</strong> grâce à l'effort et à la communauté.</li>
          <li><strong>Une meilleure mobilité articulaire</strong> sur le long terme.</li>
        </ul>

        <h2>Adapté à votre profil</h2>
        <ul>
          <li><strong>Adulte 30-50 ans en reprise</strong> après plusieurs années sans sport : 2 séances/semaine suffisent pour des progrès visibles.</li>
          <li><strong>Parent débordé</strong> : 1 heure pleine par séance, programmation variée, on n'a jamais l'impression de répéter la même chose.</li>
          <li><strong>Personne en surpoids</strong> : tout est scalable. On démarre à votre poids de corps, sans charges additionnelles.</li>
          <li><strong>Senior 60+ ans</strong> : nous avons un <strong>cours dédié seniors</strong> à intensité maîtrisée.</li>
          <li><strong>Reprise post-blessure ou post-grossesse</strong> : encadrement individualisé, mouvements adaptés.</li>
        </ul>

        <h2>Comment ça se passe ?</h2>
        <ol>
          <li><strong>Réservez vos 2 séances d'essai gratuites</strong> via le formulaire de contact.</li>
          <li><strong>Discutez de vos objectifs</strong> avec le coach (santé, perte de poids, bien-être, force, mobilité).</li>
          <li><strong>Suivez votre premier cours</strong> avec des charges et des mouvements scalés. Le coach reste à vos côtés.</li>
          <li><strong>Choisissez votre rythme</strong> : 2 ou 3 séances par semaine pour démarrer.</li>
        </ol>

        <h2>Encadrement</h2>
        <p>
          Cours encadrés par <strong>Anthony Bendahmane</strong> (Master STAPS, CrossFit Level 1) et <strong>Cédric Bastien</strong> (BPJEPS, CrossFit Level 1), tous deux anciens rugbymen et habitués à accompagner des publics en reprise.
        </p>

        <h2>Tarifs</h2>
        <p>
          <strong>2 séances d'essai gratuites</strong>. Abonnements à partir de 64 €/mois (2 séances/semaine). Réductions couples, étudiants et forces de l'ordre.
        </p>

        <h2>Questions fréquentes</h2>
        <h3>À quel âge peut-on reprendre ?</h3>
        <p>De 16 à 75 ans, avec adaptation par cours et par profil.</p>
        <h3>Combien de temps avant les premiers résultats ?</h3>
        <p>4 à 6 semaines pour l'énergie et le tonus, 8 à 12 semaines pour la force et l'endurance.</p>
        <h3>Et si j'ai mal au dos ?</h3>
        <p>Le CrossFit bien encadré renforce le dos. Les coachs adaptent les mouvements à votre situation.</p>
      </ServicePageLayout>
    </>
  );
}
